<template>
  <filterShrink v-rect-loading="loading && show" class="collapse-container" :class="show && 'show'" placement="right" ref="filterShark" :style="collapseStyle" @open="handleOpen" @close="handleClose">
    <template #default>
      <el-collapse v-model="activeNames" class="collapse-content" ref="comment">
        <el-collapse-item name="comment" ref="comment" :title="$t(`${i18nPrefix}.title.comment`)">
          <div class="comment">
            <div class="comment-input-wrapper">
              <el-input class="comment-input" v-model="comment" type="textarea" :disabled="isDisabled" :autosize="{ minRows: 4, maxRows: 7 }"/>
              <el-button class="btn add-comment-btn" :disabled="!comment || loading" @click="saveComment">{{ $t('common.btn.add') }}</el-button>
            </div>
            <div class="comment-toolbar">
              <div class="private-switch">
                <span class="label">{{ $t(`${i18nPrefix}.label.isPrivate`) }}</span>
                <el-switch
                  v-model="isPrivate"
                  active-color="#0096cc"
                  :active-text="$t('common.yes')"
                  :inactive-text="$t('common.no')"
                  :width="switchWidth"
                  :disabled="isDisabled || hasCommentOwner"
                  @change="handlePrivateSwitchChange"
                ></el-switch>
              </div>
              <div class="comment-assignment-labels">
                <span v-if="!showBadge" class="label">{{ $t('admin.talent.label.access') }}</span>
                <div class="comment-assign-popover">
                  <AssignDialog
                    :ownerInfo="ownerInfo"
                    :accountList="accountList"
                    :roleList="roleList"
                    :showInterviewerCanView="showInterviewerCanView"
                    :interviewerCanView.sync="interviewerCanView"
                    @save="handleChangeCommentOwner"
                  />
                </div>
              </div>

            </div>
          </div>
        </el-collapse-item>
        <el-collapse-item v-if="hrQuestionList?.length > 0" name="screening" ref="screening">
          <template #title>
            <span>{{ $t(`${i18nPrefix}.label.screening`) }}</span>
            <i class="iconfont iconEditor question-edit" :class="isDisabled && 'is-disabled'" @click.stop="handelEditQuestion"></i>
          </template>
          <div class="question-list">
            <template v-if="isEditQuestion && !isDisabled">
              <Question
                v-for="(question) in hrQuestionList"
                v-model="question.answer"
                :ref="`q_${question.id}`"
                :key="question.id"
                :type="question.type"
                :title="question.title"
                :surveyType="question.surveyType"
                :isRequired="question.isRequired"
                :optionList="question.optionList"
                :uploaderNeedFileDescription="question.uploaderNeedFileDescription"
                :uploaderFileDescription.sync="question.uploaderFileDescription"
                :uploaderFileTypeList="question.uploaderFileTypeList"
                :uploaderFileSize="question.uploaderFileSize"
                :uploaderTipList="question.uploaderTipList"
                :policyLink="question.policyLink"
                :errorMessage="question.errorMessage"
                :autosize="{ minRows: 4, maxRows: 7 }"
                @file-upload="handleFileUpload(question, ...arguments)"
              >
              </Question>
              <div class="btn-group">
                <el-button class="btn btn-reset" :disabled="isDisabled" @click="resetQuestion">{{ $t(`${i18nPrefix}.btn.reset`) }}</el-button>
                <el-button class="btn btn-sava" :disabled="isDisabled" @click="saveQuestion">{{ $t(`${i18nPrefix}.btn.save`) }}</el-button>
              </div>
            </template>
            <template v-else>
              <QuestionWrapper
                class="answer-question"
                isHr
                :questionnaireResultList="answerQuestionList"
                :questionLabelI18nKey="`${i18nPrefix}.label.collapseQuestion`"
                :answerLabelI18nKey="`${i18nPrefix}.label.collapseAnswer`"
                :videoLabelI18nKey="`${i18nPrefix}.label.collapseVideo`"
                :noteLabelI18nKey="`${i18nPrefix}.label.collapseNote`"
                @videoPlayIconClick="openVideoDialog"
                @viewAttachmentBtnClick="openAttachment"
              >
              </QuestionWrapper>
            </template>
          </div>
        </el-collapse-item>
        <el-collapse-item name="active" ref="active" :title="$t(`${i18nPrefix}.label.activityTracker`)">
          <div class="comment-container">
            <div
              v-for="(item, index) in commentList"
              class="comment-info"
              :key="index"
            >
              <div class="info">
                <div class="base-info">
                  <div class="name">{{ item.created_account }}</div>
                </div>
                <div class="time">{{  item.created_time|formatDateTime('YYYY/MM/DD HH:mm')  }}</div>
                <div v-if="item.job" class="job">
                  <span class="label">{{ $t(`${i18nPrefix}.label.job`) }}: </span>
                  <span class="content">{{ item.job.i18nName }}</span>
                </div>
                <div v-if="item.statusChangeText" class="status">
                  <span class="label">{{ $t(`${i18nPrefix}.label.candidateStatus`) }}: </span>
                  <span class="content">{{ item.statusChangeText }}</span>
                </div>
                <div v-if="item.cause" class="cause">
                  <span class="label">{{ $t(`${i18nPrefix}.label.cause`) }}: </span>
                  <span class="content">{{ item.cause.i18nName }}</span>
                </div>
                <template v-if="item.comment">
                  <div v-if="item.type === TAGGED_SOURCE_CHANGE_COMMENT_TYPE" class="source">
                    <span class="label">{{ $t(`${i18nPrefix}.label.activitySource`) }}: </span>
                    <span class="content">{{ item.comment }}</span>
                  </div>
                  <div v-else-if="shouldUseI18nComment(item)" class="source">
                    <span class="content">{{ formatI18nComment(item.comment) }}</span>
                  </div>
                  <div v-else class="comment">
                    <span class="label">{{ $t(`${i18nPrefix}.label.comments`) }}: </span>
                    <span class="content">{{ item.comment }}</span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>
      <DialogWrapper
        hideFooter
        appendToBody
        class="video-player-dialog"
        :title="popInfo.title"
        :show.sync="popInfo.showVideoPop"
        @close="closeVideoDialog"
      >
        <div class="video-container">
          <video v-if="popInfo.showVideoPop && popInfo.videoUrl" controls class="video" autoplay="autoplay" width="100%" :src="popInfo.videoUrl"></video>
        </div>
      </DialogWrapper>
    </template>
  </filterShrink>
</template>

<script>
import _ from 'lodash'
import filterShrink from '@/components/common/shrink/filterShrink.vue'
import Question, { QUESTION_TYPE } from '@/components/candidate/editor/question.vue'
import QuestionWrapper from './questionWrapper.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import AssignDialog from '../../../talentPool/dialog/assignDialog.vue'
import { mapActions, mapGetters } from 'vuex'
import { uploadFile } from '@/utils/qiniu'
import { QUESTION_TYPE as OPEN_QUESTION_TYPE, HAS_OPTIONS_QUESTION_TYPES } from '@/constant/openQuestion'
import { addI18nProperty, getI18nPropertyValue } from '@/i18n'
import moment from 'moment'

const OPEN_QUESTION_TYPE_MAP = {
  [OPEN_QUESTION_TYPE.VIDEO]: QUESTION_TYPE.VIDEO,
  [OPEN_QUESTION_TYPE.ATTACHMENT]: QUESTION_TYPE.ATTACHMENT,
  [OPEN_QUESTION_TYPE.SELECT]: QUESTION_TYPE.RADIO,
  [OPEN_QUESTION_TYPE.CHECKBOX]: QUESTION_TYPE.CHECKBOX,
  [OPEN_QUESTION_TYPE.TEXT]: QUESTION_TYPE.TEXTAREA,
  [OPEN_QUESTION_TYPE.SINGLE_SELECT]: QUESTION_TYPE.SELECT,
  [OPEN_QUESTION_TYPE.MULTIPLE_SELECT]: QUESTION_TYPE.MULTIPLE,
  [OPEN_QUESTION_TYPE.DATE]: QUESTION_TYPE.DATE
}

const MULTIPLE_QUESTION = [
  OPEN_QUESTION_TYPE.CHECKBOX,
  OPEN_QUESTION_TYPE.MULTIPLE_SELECT
]

const TAGGED_SOURCE_CHANGE_COMMENT_TYPE = 1
const MANUALLY_ADDING_COMMENT_TYPE = 2
const HR_MANUALLY_INVITE_CANDIDATE_TO_AGREE_CONSENT_COMMENT_TYPE = 7
const I18N_COMMENT_LIST = [
  'Updated by rating result/基于评估结果更新',
  'Updated by interview result/基于面试结果更新'
]

export default {
  components: {
    filterShrink,
    Question,
    QuestionWrapper,
    DialogWrapper,
    AssignDialog
  },
  props: {
    candidateBaseInfo: {
      required: true
    },
    isMyComment: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    isTalentPool: {
      type: Boolean,
      default: false
    },
    isPotential: {
      type: Boolean,
      default: false
    },
    showInterviewerCanView: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      MULTIPLE_QUESTION,
      HAS_OPTIONS_QUESTION_TYPES,
      OPEN_QUESTION_TYPE,
      TAGGED_SOURCE_CHANGE_COMMENT_TYPE,
      HR_MANUALLY_INVITE_CANDIDATE_TO_AGREE_CONSENT_COMMENT_TYPE,
      i18nPrefix: 'admin.application.candidate',
      loading: false,
      show: false,
      activeNames: ['screening', 'comment', 'active'],
      hrQuestionList: null,
      comment: null,
      ownerInfo: {
        account_ids: [],
        role_ids: []
      },
      isPrivate: false,
      interviewerCanView: false,
      isPrivateLocked: false,
      candidateApplicantHistoryList: [],
      comments: [],
      collapseWidth: 286,
      resetQuestionAnswer: false,
      hasCommentChangedData: false,
      hasQuestionChangedData: false,
      isEditQuestion: true,
      popInfo: {
        showVideoPop: false,
        title: null,
        videoUrl: null,
        oggUrl: null
      },
      clickCollapseName: null,
      accountList: [],
      roleList: []
    }
  },
  computed: {
    accountCount () {
      return this.ownerInfo?.account_ids?.length || 0
    },
    roleCount () {
      return this.ownerInfo?.role_ids?.length || 0
    },
    showBadge () {
      return this.accountCount > 0 || this.roleCount > 0
    },
    hasCommentOwner () {
      return this.showBadge
    },
    switchWidth () {
      return this.$i18n.locale === 'en' ? 55 : 50
    },
    candidateId () {
      return this.candidateBaseInfo.id
    },
    jobId () {
      if (this.isPotential) {
        return this.$route.params.jobId
      }
      return this.candidateBaseInfo.job?.id
    },
    commentList () {
      // 潜在候选人(isPotential=true)：存在 candidate信息时（isTalentPool=true），直接显示candidate的相关信息；
      // 反之不显示，并没有candidate_applicant_history_list数据
      const currentAccount = this.getCurrentAccount()
      if (this.isTalentPool) {
        return _
          .chain(this.comments)
          .filter(item => {
            let filterMyComment = true
            if (this.isMyComment) {
              filterMyComment = item.created_account_id === currentAccount.id
            }
            return filterMyComment && (item.comment || (item.status && item.old_status))
          })
          .map(item => {
            item.created_account = item.creator?.full_name
            return item
          })
          .value()
      }
      return _
        .chain(this.candidateApplicantHistoryList)
        .filter(item => {
          let filterMyComment = true
          if (this.isMyComment) {
            filterMyComment = item.created_account_id === currentAccount.id
          }
          return filterMyComment && (item.comment || (item.status && item.old_status))
        })
        .map(item => {
          if (item.job) {
            addI18nProperty(item.job, 'i18nName', 'cn_title', 'title')
          }
          if (item.cause) {
            addI18nProperty(item.cause, 'i18nName', 'cause_cn', 'cause_en')
          }
          if (item.status && item.old_status) {
            Object.defineProperty(item, 'statusChangeText', {
              configurable: true,
              enumerable: true,
              get () {
                const statusText = getI18nPropertyValue(item.status, 'name_cn', 'name_en')
                const oldStatusText = getI18nPropertyValue(item.old_status, 'name_cn', 'name_en')
                return `${oldStatusText} -> ${statusText}`
              }
            })
          }
          return item
        })
        .value()
    },
    collapseStyle () {
      return {
        '--collapse-width': `${this.collapseWidth}px`
      }
    },
    hasChangeData () {
      return this.hasCommentChangedData || this.hasQuestionChangedData
    },
    answerQuestionList () {
      return _.map(this.hrQuestionList, question => {
        const result = { ...question._question }
        if (HAS_OPTIONS_QUESTION_TYPES.includes(result.type)) {
          const answer = `${result.answer_value}`?.split(',')
          result.answer_option = _.filter(result.options, option => answer?.includes(`${option.id}`))
        }
        return result
      })
    }
  },
  created () {
    this.loadQuestionList()
    this.loadActivity()
    this.loadAccountList()
    this.loadRoleList()
  },
  methods: {
    ...mapActions('application', [
      'updateApplicationOpenQuestionInfo',
      'getApplicationOpenQuestionInfo',
      'handleOpenQuestionUploadedVideo',
      'getOpenQuestionUploadedVideoDuration',
      'getApplicationCandidateActivityInfo',
      'addComment'
    ]),
    ...mapActions('talent', ['getTalentCandidateActivityInfo', 'createTalentPoolComment']),
    ...mapActions('potential', ['getPotentialCandidateActivityInfo']),
    ...mapActions('common', ['getPrivateFileUrl']),
    ...mapActions('company', ['getTeamMemberList']),
    ...mapActions('roleManagement', ['getRoleList']),
    ...mapGetters('account', ['getCurrentAccount']),
    async loadQuestionList () {
      if (this.isTalentPool || this.isPotential) {
        return
      }
      this.loading = true
      try {
        const params = {
          job_id: this.jobId,
          candidate_id: this.candidateId,
          question_type: 'hr'
        }
        const resp = await this.getApplicationOpenQuestionInfo(params)
        if (resp) {
          this.hrQuestionList = this.formatOpenQuestion(resp)
          if (this.hrQuestionList?.length > 0) {
            if (!this.activeNames.includes('screening')) {
              this.activeNames.push('screening')
            }
            this.$emit('updateHrQuestion')
          }
        }
      } finally {
        this.loading = false
      }
    },
    async loadActivity () {
      this.candidateApplicantHistoryList = []
      this.comments = []
      if (this.isPotential) {
        const params = {
          potentialId: this.candidateBaseInfo.potential_id
        }
        const resp = await this.getPotentialCandidateActivityInfo(params)
        // 潜在候选人(isPotential=true)：存在 candidate信息时（isTalentPool=true），直接显示candidate的相关信息；
        // 反之不显示，并没有candidate_applicant_history_list数据
        if (resp) {
          if (this.isTalentPool) {
            this.comments = resp.comments || []
          } else {
            this.candidateApplicantHistoryList = []
          }
        }
      } else if (this.isTalentPool) {
        const params = {
          candidateId: this.candidateId
        }
        const resp = await this.getTalentCandidateActivityInfo(params)
        if (resp) {
          this.comments = resp.comments || []
        }
      } else {
        const params = {
          job_id: this.jobId,
          candidate_id: this.candidateId
        }
        const resp = await this.getApplicationCandidateActivityInfo(params)
        if (resp) {
          this.candidateApplicantHistoryList = resp.candidate_applicant_history_list || []
        }
      }
    },
    resetQuestion () {
      _.forEach(this.hrQuestionList, item => {
        item.uploaderFileDescription = null
        if (MULTIPLE_QUESTION.includes(item._question.type)) {
          item.answer = []
        } else {
          item.answer = null
        }
      })
      this.resetQuestionAnswer = true
    },
    validateUpload () {
      const errorQuestion = _.find(this.hrQuestionList, 'isUploading')
      if (errorQuestion) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.uploadTip.waitUploaded`))
      }

      if (errorQuestion) {
        const questionEl = this.$refs[`q_${errorQuestion.id}`][0]
        questionEl.$el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        return false
      }
      return true
    },
    async saveQuestion () {
      // await allUpload
      if (!this.validateUpload()) {
        return
      }
      const questionList = _.map(this.hrQuestionList, questionInfo => {
        if (questionInfo.answer && !_.isEmpty(questionInfo.answer)) {
          this.resetQuestionAnswer = false
        }
        const originQuestion = questionInfo._question
        originQuestion.answer_value = questionInfo.answer
        if ([OPEN_QUESTION_TYPE.VIDEO, OPEN_QUESTION_TYPE.ATTACHMENT].includes(originQuestion.type)) {
          originQuestion.answer_file_key = questionInfo.answer
          originQuestion.answer_value = questionInfo.uploaderFileDescription
        } else if (originQuestion.type === OPEN_QUESTION_TYPE.CHECKBOX || originQuestion.type === OPEN_QUESTION_TYPE.MULTIPLE_SELECT) {
          originQuestion.answer_value = _.join(questionInfo.answer)
        } else if (originQuestion.type === OPEN_QUESTION_TYPE.DATE) {
          originQuestion.answer_value = originQuestion.answer_value ? moment(originQuestion.answer_value).format('YYYY-MM-DD') : ''
        }
        return originQuestion
      })
      this.loading = true
      try {
        const data = {
          jobId: this.jobId,
          candidateId: this.candidateId,
          data: {
            question: { hr: questionList },
            reset_question_type_list: this.resetQuestionAnswer ? ['hr'] : null
          }
        }
        const resp = await this.updateApplicationOpenQuestionInfo(data)
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.saveQuestionSuccess`))
          this.$eventBus.$emit('refreshHrQuestion')
          await this.loadQuestionList()
          this.handleCollapseScrollTop('screening')
          this.hasQuestionChangedData = false
        }
      } finally {
        this.loading = false
        this.isEditQuestion = false
      }
    },
    handlePrivateSwitchChange () {
      if (this.isPrivate) {
        this.interviewerCanView = false
      }
    },
    async saveComment () {
      this.loading = true
      const commentInfo = {
        candidate_id: this.candidateId,
        comment: this.comment,
        is_private: this.hasCommentOwner || this.isPrivate
      }
      let addCommentMethod = this.createTalentPoolComment
      commentInfo.owner_info = this.ownerInfo
      if (!this.isTalentPool && this.showInterviewerCanView) {
        commentInfo.enable_assign = true
        commentInfo.interviewer_can_view = this.interviewerCanView
      }
      if (!this.isTalentPool) {
        commentInfo.job_id = this.jobId
        addCommentMethod = this.addComment
      }
      try {
        const resp = await addCommentMethod(commentInfo)
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.addCommentSuccess`))
          this.emitCommentAdded(this.comment)
          this.comment = null
          this.hasCommentChangedData = false
          this.isPrivate = false
          this.interviewerCanView = false
          this.ownerInfo = {
            account_ids: [],
            role_ids: []
          }
          this.isPrivateLocked = false
        }
      } finally {
        this.loading = false
      }
    },
    handleChangeCommentOwner (info, callback) {
      const accountIds = _.get(info, 'account_ids', [])
      const roleIds = _.get(info, 'role_ids', [])
      if (accountIds?.length || roleIds?.length) {
        this.isPrivate = true
      }
      this.ownerInfo = info
      callback()
    },
    emitCommentAdded (comment) {
      this.$eventBus.$emit('onCommentAdded', comment)
    },
    onCommentAdded (comment) {
      const currentAccount = this.getCurrentAccount()
      if (this.isTalentPool) {
        this.comments.splice(0, 0, {
          created_time: new Date(),
          created_account: currentAccount.full_name,
          created_account_id: currentAccount.id,
          creator: {
            full_name: currentAccount.full_name
          },
          comment: comment,
          type: MANUALLY_ADDING_COMMENT_TYPE
        })
      } else if (!this.isPotential) {
        const addComment = {
          created_time: new Date(),
          created_account: currentAccount.full_name,
          created_account_id: currentAccount.id,
          comment: comment,
          type: MANUALLY_ADDING_COMMENT_TYPE
        }
        if (this.candidateBaseInfo.job) {
          addComment.job = addI18nProperty({
            ...this.candidateBaseInfo.job
          }, 'i18nName', 'cn_title', 'title')
        }
        this.candidateApplicantHistoryList.splice(0, 0, addComment)
      }
    },
    shouldUseI18nComment (item) {
      return item.type === HR_MANUALLY_INVITE_CANDIDATE_TO_AGREE_CONSENT_COMMENT_TYPE || I18N_COMMENT_LIST.includes(item.comment)
    },
    formatI18nComment (comment) {
      const comments = _.split(comment, '/')
      const commentEn = comments[0]
      const commentCn = comments[1]
      if (this.$i18n.locale === 'en') {
        return commentEn
      } else {
        return commentCn
      }
    },
    getFileUID (prefix) {
      const time = new Date().getTime().toString(32)
      const random = _.times(5, () => Math.floor(Math.random() * 65535).toString(32)).join('')
      const counter = _.uniqueId().toString(32)
      return (prefix || 'o_') + time + random + counter
    },
    async handleFileUpload (questionInfo, file, callback) {
      let errorMessage = null
      if (questionInfo.uploaderFileTypeList && !questionInfo.uploaderFileTypeList.includes(file.type)) {
        errorMessage = this.$t(`${this.i18nPrefix}.message.wringFileType`)
      } else if (questionInfo.uploaderFileSize && file.size > questionInfo.uploaderFileSize) {
        errorMessage = this.$t(`${this.i18nPrefix}.message.wringFileSize`)
      }
      if (errorMessage) {
        questionInfo.errorMessage = errorMessage
        callback()
        return
      }
      questionInfo.isUploading = true
      const fileName = file.name
      const ext = fileName.slice(fileName.indexOf('.'))
      const fileKey = `${this.getFileUID()}${ext}`
      await uploadFile(file, fileKey, false)
      const originQuestion = questionInfo._question
      if (originQuestion.type === OPEN_QUESTION_TYPE.VIDEO) {
        let resp = await this.handleOpenQuestionUploadedVideo({
          file_key: fileKey
        })
        const persistentId = resp.persistent_id
        const maxDuration = parseFloat(originQuestion.duration.slice(0, -1))
        if (maxDuration) {
          let duration = resp.duration
          let isFinished = resp.is_finished
          while (!isFinished) {
            resp = await this.getOpenQuestionUploadedVideoDuration({ persistent_id: persistentId })
            duration = resp.duration
            isFinished = resp.is_finished
            await new Promise((resolve, reject) => {
              setTimeout(resolve, 1000)
            })
          }
          if (duration > maxDuration) {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.questionVideoIsTooLong`, { duration: originQuestion.duration }))
          }
        }
      }
      callback()
      questionInfo.answer = fileKey
      questionInfo.isUploading = false
    },
    handelEditQuestion () {
      this.isEditQuestion = true
      if (!this.activeNames.includes('screening')) {
        this.activeNames.push('screening')
      }
    },
    closeVideoDialog () {
      this.popInfo.showVideoPop = false
      this.popInfo.title = null
      this.popInfo.videoUrl = null
      this.popInfo.oggUrl = null
    },
    async openVideoDialog (question, callback) {
      const resp = await this.getPrivateFileUrl({ fileType: 'OPEN_QUESTION_VIDEO', fileKey: question.answer_file_key })
      const popInfo = this.popInfo
      popInfo.title = question.title
      popInfo.videoUrl = resp.video
      popInfo.oggUrl = resp.ogg
      callback()
      popInfo.showVideoPop = true
    },
    async openAttachment (question, callback) {
      const fileUrl = await this.getPrivateFileUrl({ fileType: 'OPEN_QUESTION_ATTACHMENT', fileKey: question.answer_file_key })
      const candidateName = this.candidateBaseInfo.other_name
      const jobName = getI18nPropertyValue(this.candidateBaseInfo.job, 'cn_title', 'title')
      const fileExt = question.answer_file_key.split('.')[1]
      let fileName = `${candidateName}-${jobName}-${question.title}`
      if (fileExt) {
        fileName += '.' + fileExt
      }
      window.open(`${fileUrl}&attname=${encodeURIComponent(fileName)}`)
      callback()
    },
    formatOpenQuestion (questionList) {
      const vm = this
      return _.map(questionList, question => {
        const openQuestionType = question.type
        const questionType = OPEN_QUESTION_TYPE_MAP[openQuestionType]
        let answer = question.answer_value
        let uploaderFileTypeList = null
        const uploaderFileSize = 200 * 1024 * 1024
        let uploaderFileDescription = null
        if (openQuestionType === OPEN_QUESTION_TYPE.SELECT || openQuestionType === OPEN_QUESTION_TYPE.SINGLE_SELECT) {
          answer = answer && parseInt(answer)
        } else if (openQuestionType === OPEN_QUESTION_TYPE.CHECKBOX || openQuestionType === OPEN_QUESTION_TYPE.MULTIPLE_SELECT) {
          answer = _.chain(answer).split(',').map(item => parseInt(item)).filter().value()
        } else if (openQuestionType === OPEN_QUESTION_TYPE.VIDEO) {
          uploaderFileTypeList = ['video/mp4', 'video/x-msvideo', 'video/ogg', 'video/quicktime']
          uploaderFileDescription = question.answer_value
          answer = question.answer_file_key
        } else if (openQuestionType === OPEN_QUESTION_TYPE.ATTACHMENT) {
          uploaderFileDescription = question.answer_value
          answer = question.answer_file_key
        }
        if (answer?.length > 0 || uploaderFileDescription) {
          this.isEditQuestion = false
        }
        const questionInfo = {
          _question: question,
          id: question.id,
          type: questionType,
          title: question.title,
          isRequired: false,
          answer,
          surveyType: question.survey_type,
          optionList: _.map(question.options, option => {
            return {
              label: option.description,
              value: option.id
            }
          }),
          uploaderFileTypeList,
          uploaderFileSize,
          uploaderNeedFileDescription: [OPEN_QUESTION_TYPE.VIDEO, OPEN_QUESTION_TYPE.ATTACHMENT].includes(openQuestionType),
          uploaderFileDescription,
          errorMessage: null
        }
        if (openQuestionType === OPEN_QUESTION_TYPE.VIDEO) {
          Object.defineProperty(questionInfo, 'uploaderTipList', {
            configurable: true,
            enumerable: true,
            get () {
              return [
                vm.$t(`${vm.i18nPrefix}.message.uploadTip.openQuestionVideo`, { duration: question.duration })
              ]
            }
          })
        } else if (openQuestionType === OPEN_QUESTION_TYPE.ATTACHMENT) {
          Object.defineProperty(questionInfo, 'uploaderTipList', {
            configurable: true,
            enumerable: true,
            get () {
              return [
                vm.$t(`${vm.i18nPrefix}.message.uploadTip.attachment`, { size: 200 })
              ]
            }
          })
        }
        return questionInfo
      })
    },
    handleOpen () {
      this.show = true
      this.$emit('open', this.collapseWidth)
    },
    handleClose () {
      this.show = false
      this.$emit('close', this.collapseWidth)
    },
    handelChangeShowCollapse (activeName) {
      const filterShark = this.$refs.filterShark
      if (filterShark && (!this.show || this.clickCollapseName === activeName)) {
        filterShark.handelClick()
      } else {
        this.activeNames = ['comment', 'screening', 'active']
      }
      if (this.show) {
        this.handleCollapseScrollTop(activeName)
      }
      this.clickCollapseName = activeName
    },
    handleCollapseScrollTop (activeName) {
      const ref = this.$refs[activeName]
      const dom = document.querySelector('.shrink-content .content')
      if (ref && dom) {
        dom.scrollTop = ref.$el.offsetTop - 23
      }
    },
    checkRouteCanLeave () {
      let canLeave = true
      if (this.hasChangeData) {
        canLeave = window.confirm(this.$t(`${this.i18nPrefix}.message.refreshPageWithoutSaveData`))
      }
      return canLeave
    },
    async loadAccountList () {
      const account = this.getCurrentAccount()
      const params = {
        companyId: account.root_company_id,
        is_all: true,
        exclude_disabled: true
      }
      const resp = await this.getTeamMemberList(params)
      if (resp) {
        this.accountList = _.orderBy(resp.results || [], [item => (item.name || '').toLowerCase()], ['asc'])
      }
    },
    async loadRoleList () {
      const resp = await this.getRoleList()
      if (resp) {
        this.roleList = _.map(resp.role_list || [], role => ({
          id: role.id,
          name: role.title
        }))
      }
    }
  },
  watch: {
    comment: {
      handler () {
        if (this.comment) {
          this.hasCommentChangedData = true
          this.clickCollapseName = null
        }
      }
    },
    hrQuestionList: {
      deep: true,
      handler (newVal, oldVla) {
        if (oldVla) {
          this.hasQuestionChangedData = true
          this.clickCollapseName = null
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.collapse-container {
  .collapse-content {
    padding: 16px;
    width: var(--collapse-width);

    .comment {
      .comment-input-wrapper {
        position: relative;

        .comment-input {
          display: block;

          ::v-deep {
            .el-textarea__inner {
              padding-right: 72px;
              padding-bottom: 42px;
            }
          }
        }

        .add-comment-btn {
          position: absolute;
          right: 8px;
          bottom: 8px;
          font-size: 12px;
          padding: 0 10px;
          height: 28px;
          padding: 0px 8px;
        }
      }

      .btn {
        color: #fff;
        background-color: #0096cc;
        border-color: #0096cc;
        height: 32px;
        padding: 0 16px;

        &.is-disabled {
          background-color: #909399;
          border-color: #909399;
          color: #fff;
        }
      }

      .comment-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-top: 16px;

        .private-switch {
          display: inline-flex;
          align-items: center;

          .label {
            font-size: 12px;
            margin-right: 10px;
          }

          .el-switch {
            ::v-deep {
              .el-switch__label {
                display: none;
                position: absolute;
                height: 100%;
                color: #ffffff;
                z-index: 1;
                margin: 0;

                &.is-active {
                  display: inline-block;
                }
              }

              .el-switch__label--left {
                left: 25px;
              }

              .el-switch__core {
                width: 55px;
              }

              .el-switch__label--right {
                left: 10px;
              }
            }
          }
        }

        .comment-assignment-labels {
          display: inline-flex;
          align-items: center;
          font-size: 12px;

          .label {
            margin-right: 14px;
          }
        }

      }
    }

    .question-edit {
      font-size: 18px;
      color: #0096cc;
      margin-left: 16px;

      &:hover {
        color: #33abd6;
      }

      &.is-disabled {
        color: #c0c4cc;
        cursor: not-allowed;

        &:hover {
          color: #c0c4cc;
        }
      }
    }
    .btn-group {
      margin-top: 20px;
      text-align: right;

      .private-switch {
        .label {
          margin-right: 10px;
        }
      }

      .btn {
        color: #fff;
        background-color: #0096cc;
        border-color: #0096cc;
        height: 32px;
        padding: 0 16px;

        &.is-disabled {
          background-color: #909399;
          border-color: #909399;
          color: #fff;
        }
      }

      .btn-reset {
        color: #0096cc;
        background-color: #fff;
      }

      .btn-sava {
        color: #fff;
        background-color: #0096cc;
      }
    }

    .comment-container {
      .comment-info {
        padding: 10px 0;
        border-bottom: 1px solid #ebeef5;

        .info {
          font-size: 12px;
          line-height: 18px;
          color: #606266;

          .base-info {
            .name {
              font-weight: bold;
              font-size: 14px;
              line-height: 20px;
            }
          }

          .label {
            font-weight: bold;
            display: block;
          }

          .comment {
            .content {
              white-space: pre-wrap;
            }
          }
        }

        &:first-child {
          padding-top: 0;
        }

        &:last-child {
          padding-bottom: 0;
          border-bottom: none;
        }
      }
    }
  }

  ::v-deep {
    &.shrink-container {
      position: sticky;
      top: 0;
      right: 0;
      flex-shrink: 0;
    }

    .shrink-content {
      width: 0;
      overflow: hidden;
      transition: width 0.5s ease-in-out;

      .content {
        overflow-x: hidden;
        width: var(--collapse-width);

        .el-collapse {
          border-bottom: none;
        }
      }
    }

    .collapse-content {
      .el-collapse-item__header {
        font-weight: bold;
        color: #606266;
        font-size: 18px;
        line-height: 22px;
        background-color: #f7f8fa;
        border-bottom: 1px solid #a9a9a9;
      }

      .el-collapse-item__content {
        padding-top: 20px;
        color: #606266;
        background-color: #f7f8fa;

        .comment {
          .el-textarea__inner {
            color: #606266;
          }
        }

        .question-container {
          font-size: 14px;

          .question-title-container {
            display: flex;

            .question-title {
              height: initial;
              font-size: 14px;
              line-height: 18px;

              &:first-child {
                margin-right: 4px;
                flex-shrink: 0;
              }
            }
          }

          .question-answer-wrapper {
            .question-answer {
              .question-option {
                margin-top: 0;
              }

              .el-radio {
                display: flex;
                line-height: 22px;

                .el-radio__input {
                  top: 4px;
                }

                .el-radio__label {
                  white-space: pre-wrap;
                  word-wrap: break-word;
                }
              }

              .el-checkbox {
                display: flex;
                line-height: 22px;

                .el-checkbox__input {
                  top: 2px;
                }

                .el-checkbox__label {
                  white-space: pre-wrap;
                  word-wrap: break-word;
                }
              }

              .upload-btn {
                height: 30px;
                padding: 0 15px;
                background-color: #0096cc;
              }

              .el-textarea__inner {
                color: #606266;
              }

              .upload-tip {
                .tip-message {
                  color: #909399;
                }
              }
            }
          }
        }

        .answer-question {
          .question-item {
            margin: 0;
            padding: 10px 0;
            border-bottom: 1px solid #ebeef5;

            &:first-child {
              padding-top: 0;
            }

            &:last-child {
              padding-bottom: 0;
              border: none;
            }

            .question-container {
              padding: 0;
              margin: 0;
              border: none;

              .question {
                margin-bottom: 8px;

                .label {
                  width: 36px;
                }

                .content {
                  margin-left: 4px;
                }
              }

              .answer {
                margin-top: 0;

                .label {
                  width: 36px;
                }

                .content {
                  margin-left: 4px;
                }

                .video-container {
                  width: 120px;
                  height: 60px;
                }
              }

              .remarks {
                margin-top: 8px;

                .label {
                  width: 36px;
                }

                .content {
                  margin-left: 4px;
                }
              }
            }
          }
        }
      }

      .el-collapse-item__wrap {
        border-bottom: none;
      }
    }
  }

  &.show {
    ::v-deep {
      .shrink-content {
        width: var(--collapse-width);
        overflow: hidden auto;
      }
    }
  }

  ::-webkit-scrollbar {
    width: 10px;
    background: none;
  }
}

.video-player-dialog {
  ::v-deep .el-dialog__body {
    height: 60vh;
  }

  .video-container {
    height: calc(100% - 50px);
    text-align: center;

    .video {
      width: 100%;
      height: 100%;
    }
  }
}

.page-en {
  .collapse-container {
    ::v-deep {
      .collapse-content {
        .el-collapse-item__content{
          .answer-question {
            .question-item {
              .question-container {
                .question {
                  .label {
                    width: 18px;
                  }
                }

                .answer {
                  .label {
                    width: 18px;
                  }
                }

                .remarks {
                  .label {
                    width: 18px;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
